import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Zap, Loader2, QrCode, Download, Percent, CheckCircle2, Copy, X } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from './adminConstants';
import { PLAN_CATALOG } from '@/constants/plans';
import AdminBatchImplant from './AdminBatchImplant';
import AdminDiscountAudit from './AdminDiscountAudit';
import AdminQuickLinkBroadcast from './AdminQuickLinkBroadcast';
import AdminRecoveryFunnel from './AdminRecoveryFunnel';
import AdminRecruitLinks from './AdminRecruitLinks';
import AdminCustomBroadcast from './AdminCustomBroadcast';
import AdminFeiraPromos from './AdminFeiraPromos';

const PAID_PLANS = PLAN_CATALOG.filter(p => p.id !== 'free' && p.price > 0);

const AdminImplantTab = () => {
  const { getToken } = useAuth();
  const [form, setForm] = useState({ company_name: '', owner_name: '', whatsapp: '', cpf_cnpj: '', owner_email: '' });
  const [planId, setPlanId] = useState('enterprise');
  const [closingMode, setClosingMode] = useState('boleto');
  const [firstDueDate, setFirstDueDate] = useState('');
  const [discountPct, setDiscountPct] = useState(0);
  const [discountReason, setDiscountReason] = useState('');
  const [options, setOptions] = useState([]);
  const [newPct, setNewPct] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [feira, setFeira] = useState(null);

  const authHeaders = useCallback(() => ({ headers: { Authorization: `Bearer ${getToken()}` } }), [getToken]);

  useEffect(() => {
    axios.get(`${API}/admin/discount-options`, authHeaders())
      .then(r => setOptions(r.data.options || []))
      .catch(() => {});
    axios.get(`${API}/admin/feira-stats`, authHeaders())
      .then(r => setFeira(r.data))
      .catch(() => {});
  }, [authHeaders]);

  const saveOptions = async (next) => {
    try {
      const r = await axios.put(`${API}/admin/discount-options`, { options: next }, authHeaders());
      setOptions(r.data.options);
      toast.success('Percentuais atualizados');
    } catch { toast.error('Erro ao salvar percentuais'); }
  };

  const isPaid = closingMode !== 'trial';
  const plan = PLAN_CATALOG.find(p => p.id === planId);
  const finalPrice = plan ? plan.price * (1 - discountPct / 100) : 0;
  const fmt = (v) => `R$ ${v.toFixed(2).replace('.', ',')}`;

  const submit = async (e, networkConfirmed = false) => {
    e?.preventDefault?.();
    setSubmitting(true);
    try {
      const payload = {
        ...form, plan_id: planId, closing_mode: closingMode,
        first_due_date: closingMode === 'boleto' ? (firstDueDate || null) : null,
        discount_pct: discountPct || null, discount_reason: discountReason || null,
        network_confirmed: networkConfirmed,
      };
      const r = await axios.post(`${API}/admin/implant-client`, payload, authHeaders());
      setResult(r.data);
      toast.success('Cliente cadastrado! Kit enviado no WhatsApp 🎉');
      if (r.data?.owner_relinked) toast.info(r.data.message, { duration: 9000 });
      setForm({ company_name: '', owner_name: '', whatsapp: '', cpf_cnpj: '', owner_email: '' });
      setFirstDueDate('');
      setDiscountPct(0); setDiscountReason('');
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d?.network_confirm_required) {
        setSubmitting(false);
        if (window.confirm(`${d.message}\n\nToque OK para cadastrar como nova unidade da mesma rede (mesmo dono e mesmo login).`)) {
          return submit(null, true);
        }
        return;
      }
      toast.error(typeof d === 'string' ? d : 'Erro ao cadastrar');
    } finally { setSubmitting(false); }
  };

  const feiraUrl = `${window.location.origin}/feira`;
  const downloadQr = () => {
    const canvas = document.getElementById('feira-qr-canvas');
    if (!canvas) return;
    const a = document.createElement('a');
    a.download = 'qr-feira-falocomodono.png';
    a.href = canvas.toDataURL('image/png');
    a.click();
  };

  const inputCls = "w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#C5A059]";

  return (
    <div className="space-y-6" data-testid="admin-implant-tab">
      <div className="grid lg:grid-cols-3 gap-6">
        {/* WIZARD */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg"><Zap className="w-5 h-5 text-[#C5A059]" /> Cadastro Relâmpago</CardTitle>
            <p className="text-sm text-slate-500">3 campos, fechamento na hora. O cliente recebe magic link + placa no WhatsApp automaticamente.</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <input required placeholder="Nome da empresa *" value={form.company_name} data-testid="implant-company-name"
                  onChange={e => setForm({ ...form, company_name: e.target.value })} className={inputCls} />
                <input required placeholder="Nome do dono *" value={form.owner_name} data-testid="implant-owner-name"
                  onChange={e => setForm({ ...form, owner_name: e.target.value })} className={inputCls} />
                <input required placeholder="WhatsApp do dono (DDD + número) *" value={form.whatsapp} data-testid="implant-whatsapp"
                  onChange={e => setForm({ ...form, whatsapp: e.target.value })} className={inputCls} />
                <input placeholder="E-mail (opcional)" type="email" value={form.owner_email} data-testid="implant-email"
                  onChange={e => setForm({ ...form, owner_email: e.target.value })} className={inputCls} />
              </div>

              {/* Fechamento */}
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Fechamento</p>
                <div className="grid grid-cols-3 gap-2">
                  {[['boleto', '📄 Boleto D+3'], ['pix', '⚡ PIX agora'], ['trial', '🎁 Trial grátis']].map(([m, label]) => (
                    <button key={m} type="button" data-testid={`closing-${m}`}
                      onClick={() => setClosingMode(m)}
                      className={`py-2.5 rounded-lg text-sm font-semibold border transition-colors ${closingMode === m ? 'bg-[#0A2647] text-white border-[#0A2647]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#C5A059]'}`}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {isPaid && (
                <>
                  {/* Planos */}
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Plano</p>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                      {PAID_PLANS.map(p => (
                        <button key={p.id} type="button" data-testid={`plan-${p.id}`}
                          onClick={() => setPlanId(p.id)}
                          className={`relative p-2.5 rounded-lg border text-center transition-colors ${planId === p.id ? 'bg-[#0A2647] text-white border-[#0A2647]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#C5A059]'}`}>
                          {p.id === 'enterprise' && <span className="absolute -top-2 left-1/2 -translate-x-1/2 text-[9px] bg-[#C5A059] text-[#0A2647] font-bold px-1.5 py-0.5 rounded-full whitespace-nowrap">MAIS VENDIDO</span>}
                          <p className="text-xs font-bold">{p.label}</p>
                          <p className="text-[11px] opacity-80">{fmt(p.price)}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  <input required={isPaid} placeholder="CPF/CNPJ do cliente * (para a cobrança)" value={form.cpf_cnpj} data-testid="implant-cnpj"
                    onChange={e => setForm({ ...form, cpf_cnpj: e.target.value })} className={inputCls} />

                  {/* Data do 1º boleto (período grátis) */}
                  {closingMode === 'boleto' && (
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Data do 1º boleto (opcional)</p>
                      <input type="date" value={firstDueDate} min={new Date().toISOString().slice(0, 10)} data-testid="implant-first-due-date"
                        onChange={e => setFirstDueDate(e.target.value)} className={inputCls} />
                      <p className="text-[11px] text-slate-400 mt-1">
                        Deixe vazio para o padrão (vence em 3 dias). Escolha uma data futura para dar um <strong>período grátis</strong>: o cliente usa desde já e o 1º boleto vence nessa data — os próximos vêm a cada 30 dias depois dela.
                      </p>
                    </div>
                  )}

                  {/* Desconto */}
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Desconto recorrente (opcional)</p>
                    <div className="flex flex-wrap gap-2 items-center">
                      <button type="button" onClick={() => setDiscountPct(0)} data-testid="discount-0"
                        className={`px-3 py-1.5 rounded-full text-sm font-semibold border ${discountPct === 0 ? 'bg-[#0A2647] text-white border-[#0A2647]' : 'bg-white text-slate-600 border-slate-200'}`}>Sem desconto</button>
                      {options.map(o => (
                        <button key={o} type="button" onClick={() => setDiscountPct(o)} data-testid={`discount-${o}`}
                          className={`px-3 py-1.5 rounded-full text-sm font-semibold border ${discountPct === o ? 'bg-[#C5A059] text-[#0A2647] border-[#C5A059]' : 'bg-white text-slate-600 border-slate-200'}`}>{o}%</button>
                      ))}
                    </div>
                    {discountPct > 0 && (
                      <input placeholder="Motivo (ex: grupo 3 lojas)" value={discountReason} data-testid="discount-reason"
                        onChange={e => setDiscountReason(e.target.value)} className={`${inputCls} mt-2`} />
                    )}
                  </div>

                  {/* Resumo */}
                  <div className="bg-slate-50 rounded-lg p-3 flex items-center justify-between" data-testid="price-summary">
                    <span className="text-sm text-slate-600">{plan?.label} {discountPct > 0 && <span className="text-[#C5A059] font-semibold">−{discountPct}%</span>}</span>
                    <span className="text-lg font-bold text-[#0A2647]">{fmt(finalPrice)}<span className="text-xs font-normal text-slate-400">/mês para sempre</span></span>
                  </div>
                </>
              )}

              <Button type="submit" disabled={submitting} data-testid="implant-submit-btn"
                className="w-full bg-[#C5A059] hover:bg-[#b8934d] text-[#0A2647] font-bold py-6 text-base">
                {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : closingMode === 'trial' ? 'Cadastrar com Trial 🎁' : closingMode === 'pix' ? 'Fechar com PIX ⚡' : 'Fechar com Boleto D+3 📄'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Coluna direita: QR Feira + config descontos */}
        <div className="space-y-6">
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2 text-base"><QrCode className="w-5 h-5 text-[#C5A059]" /> QR da Feira</CardTitle></CardHeader>
            <CardContent className="text-center space-y-3">
              <div className="inline-block bg-white p-3 rounded-xl border border-slate-200">
                <QRCodeCanvas id="feira-qr-canvas" value={feiraUrl} size={180} fgColor="#0A2647" />
              </div>
              <p className="text-xs text-slate-500 break-all">{feiraUrl}</p>
              <div className="flex gap-2 justify-center">
                <Button variant="outline" size="sm" onClick={downloadQr} data-testid="download-feira-qr"><Download className="w-4 h-4 mr-1" /> Baixar PNG</Button>
                <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(feiraUrl); toast.success('Link copiado'); }} data-testid="copy-feira-link"><Copy className="w-4 h-4 mr-1" /> Copiar link</Button>
              </div>
              <p className="text-[11px] text-slate-400">O interessado escaneia, preenche 3 campos e recebe o kit no WhatsApp na hora.</p>
              {feira && (
                <div className="pt-2 border-t border-slate-100 text-left space-y-2" data-testid="feira-stats">
                  <div className="flex justify-around text-center">
                    {[['Hoje', feira.today], ['7 dias', feira.week], ['Total', feira.total]].map(([l, v]) => (
                      <div key={l}><p className="text-xl font-bold text-[#0A2647]">{v}</p><p className="text-[10px] text-slate-400 uppercase">{l}</p></div>
                    ))}
                  </div>
                  {(feira.recent || []).length > 0 && (
                    <div className="max-h-28 overflow-y-auto space-y-1">
                      {feira.recent.slice(0, 6).map((r, i) => (
                        <p key={i} className="text-[11px] text-slate-500 truncate">
                          <b className="text-[#0A2647]">{r.name}</b> · {r.whatsapp} · {r.created_at ? new Date(r.created_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2 text-base"><Percent className="w-5 h-5 text-[#C5A059]" /> Percentuais de Desconto</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap gap-2">
                {options.map(o => (
                  <span key={o} className="inline-flex items-center gap-1 bg-slate-100 rounded-full px-3 py-1 text-sm font-semibold text-[#0A2647]" data-testid={`option-chip-${o}`}>
                    {o}%
                    <button onClick={() => saveOptions(options.filter(x => x !== o))} className="text-slate-400 hover:text-red-500" data-testid={`remove-option-${o}`}><X className="w-3.5 h-3.5" /></button>
                  </span>
                ))}
                {options.length === 0 && <p className="text-xs text-slate-400">Nenhum percentual configurado</p>}
              </div>
              <div className="flex gap-2">
                <input type="number" min="1" max="99" placeholder="Ex: 20" value={newPct} data-testid="new-option-input"
                  onChange={e => setNewPct(e.target.value)} className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-sm" />
                <Button size="sm" variant="outline" data-testid="add-option-btn"
                  onClick={() => { const v = parseFloat(newPct); if (v > 0 && v < 100) { saveOptions([...options, v]); setNewPct(''); } }}>Adicionar</Button>
              </div>
              <p className="text-[11px] text-slate-400">Esses são os únicos percentuais que afiliados/parceiros poderão aplicar.</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* RESULTADO */}
      {result && (
        <Card className="border-green-200 bg-green-50/50" data-testid="implant-result">
          <CardContent className="pt-6 space-y-3">
            <p className="flex items-center gap-2 font-bold text-green-700"><CheckCircle2 className="w-5 h-5" /> Cliente cadastrado! Kit (magic link + placa) enviado no WhatsApp.</p>
            {result.mode !== 'trial' && (
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="text-sm space-y-1">
                  <p><b>Plano:</b> {result.plan_name} — <b>{`R$ ${result.final_price?.toFixed(2).replace('.', ',')}`}/mês</b>{result.discount_pct > 0 && ` (−${result.discount_pct}%)`}</p>
                  {result.mode === 'boleto' && <p><b>Boleto vence:</b> {result.due_date}</p>}
                  {result.payment_url && <a href={result.payment_url} target="_blank" rel="noreferrer" className="text-[#0A2647] underline font-semibold" data-testid="payment-url-link">Ver fatura / boleto →</a>}
                </div>
                {result.pix_qr?.encodedImage && (
                  <div className="text-center">
                    <p className="text-xs font-semibold text-slate-500 mb-1">Cliente escaneia e paga agora:</p>
                    <img src={`data:image/png;base64,${result.pix_qr.encodedImage}`} alt="PIX QR" className="w-40 h-40 mx-auto border rounded-lg" data-testid="pix-qr-img" />
                  </div>
                )}
              </div>
            )}
            <div className="text-xs text-slate-500 bg-white rounded-lg p-3 border border-slate-200">
              <p><b>Acesso do cliente:</b> {result.credentials?.email}{result.credentials?.password
                ? <> / senha: <code className="font-mono">{result.credentials.password}</code></>
                : <> / <b>mesma senha da unidade anterior</b> (dono de rede — as lojas aparecem no seletor do painel)</>}
                {' '}(ele também entra sem senha pelo magic link do WhatsApp)</p>
              <p><b>Página de avaliação:</b> falocomodono.com.br/{result.slug} · <a href={`${API.replace('/api', '')}/api/placa/${result.slug}`} target="_blank" rel="noreferrer" className="underline" data-testid="placa-link">ver placa</a></p>
            </div>
            <Button variant="outline" size="sm" onClick={() => setResult(null)} data-testid="close-result-btn">Novo cadastro</Button>
          </CardContent>
        </Card>
      )}

      <AdminQuickLinkBroadcast />
      <AdminFeiraPromos />

      <AdminCustomBroadcast />
      <AdminRecruitLinks />
      <AdminRecoveryFunnel />
      <AdminBatchImplant />
      <AdminDiscountAudit />
    </div>
  );
};

export default AdminImplantTab;
